interface ComponentStats {
  count: number
  totalMemory: number
  watchers: number
  dataKeys: number
  visible: number
  hidden: number
  children: { name: string; memory: number }[]
  keySizes: { key: string; memory: number }[]
}

interface MemoryStats {
  'Component Type': string
  Instances: number | string
  'Total Memory': string
  'Avg Memory/Instance': string
  'Total Watchers': number | string
  'Avg Watchers/Instance': string
  'Data Keys': number | string
  'Visible/Hidden': string
  Type: string
  Children: { name: string; memory: string }[]
  KeySizes: { key: string; memory: string }[]
}

export class MemoryAnalyzer {
  private static seen = new Set<any>()
  private static components = new Map<string, ComponentStats>()

  public static async register(): Promise<void> {
    try {
      ;(window as any).analyzeMemory = this.analyze.bind(this)
      console.log('[Memory Analyzer] Successfully registered. Use analyzeMemory() to analyze components.')
    } catch (error) {
      console.error('[Memory Analyzer] Failed to register:', error)
    }
  }

  private static formatBytes(bytes) {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  private static getObjectSize(obj: any): number {
    if (!obj || this.seen.has(obj)) return 0
    this.seen.add(obj)

    let size = 0

    if (typeof obj === 'string') return obj.length * 2
    if (typeof obj === 'number') return 8
    if (typeof obj === 'boolean') return 4

    if (Array.isArray(obj)) {
      return obj.reduce((sum, item) => sum + this.getObjectSize(item), 0)
    }

    if (typeof obj === 'object') {
      try {
        Object.keys(obj).forEach(key => {
          if (typeof obj[key] !== 'function') {
            size += key.length * 2
            size += this.getObjectSize(obj[key])
          }
        })
      } catch (e) {
        /* Ignore errors */
      }
    }

    return size
  }

  private static isElementVisible(el: any): boolean {
    if (!el || !el.nodeType) return false

    try {
      if (el._vnode) {
        el = el._vnode.el
      }

      if (!el || !el.nodeType) return false

      const style = window.getComputedStyle(el)
      return (
        style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0' && el.offsetParent !== null
      )
    } catch (e) {
      return false
    }
  }

  private static analyzeComponent(vm: any): { memory: number; name: string } {
    if (!vm || this.seen.has(vm)) return { memory: 0, name: '' }
    this.seen.add(vm)

    const name = vm.$options?.name || vm.$options?._componentTag || 'Anonymous'
    let memory = 0

    const keySizes: { key: string; memory: number }[] = []
    if (vm.$data) {
      Object.keys(vm.$data).forEach(key => {
        try {
          const keyMemory = this.getObjectSize(vm.$data[key])
          keySizes.push({ key, memory: keyMemory })
          memory += keyMemory
        } catch (error) {}
      })
    }
    if (vm.$props) {
      Object.keys(vm.$props).forEach(key => {
        try {
          const propMemory = this.getObjectSize(vm.$props[key])
          keySizes.push({ key: `(prop) ${key}`, memory: propMemory })
          memory += propMemory
        } catch (error) {}
      })
    }
    if (vm.$options?.computed) memory += Object.keys(vm.$options.computed).length * 100
    if (vm._watchers) memory += vm._watchers.length * 100

    if (!this.components.has(name)) {
      this.components.set(name, {
        count: 0,
        totalMemory: 0,
        watchers: 0,
        dataKeys: 0,
        visible: 0,
        hidden: 0,
        children: [],
        keySizes: [],
      })
    }

    const stats = this.components.get(name)!

    const childSize: { name: string; memory: number }[] = []
    const children = vm.$children || vm.subTree?.children || []
    if (Array.isArray(children)) {
      children.forEach((child: any) => {
        if (!this.seen.has(child)) {
          const childDetails = this.analyzeComponent(child)
          memory += childDetails.memory
          childSize.push(childDetails)
        }
      })
    }
    stats.children = childSize

    stats.count++
    stats.totalMemory += memory
    stats.watchers += vm._watchers?.length || 0
    stats.dataKeys += vm.$data ? Object.keys(vm.$data).length : 0
    stats.keySizes = keySizes

    try {
      if (vm.$el) {
        if (this.isElementVisible(vm.$el)) {
          stats.visible++
        } else {
          stats.hidden++
        }
      } else {
        stats.hidden++
      }
    } catch (e) {
      stats.hidden++
    }

    return { memory, name }
  }

  private static findMicroFrontends(): Array<{ name: string; mountId: string }> {
    return Object.keys(microFrontendConfig)
      .map(name => ({
        name,
        mountId: this.getMountId(name),
      }))
      .filter((app): app is { name: string; mountId: string } => app.mountId !== null)
  }

  private static getMountId(appName: string): string | null {
    // Common mount point patterns
    const mountPatterns = {
      documentsApp: 'document-management-app',
      customObjectsApp: 'contact-associations-app',
      // Add other special cases here
    }

    // First check for known mappings
    if (mountPatterns[appName]) {
      return mountPatterns[appName]
    }

    // Then try default patterns
    const defaultId = appName
      .replace('App', '')
      .toLowerCase()
      .replace(/([A-Z])/g, '-$1')
      .toLowerCase()

    const mountId = `${defaultId}-app`
    return document.getElementById(mountId) ? mountId : null
  }

  private static isMicroFrontendMounted(mountId: string): boolean {
    const mountElement = document.getElementById(mountId)
    return !!(mountElement && mountElement.children.length > 0)
  }

  public static analyze(): void {
    console.log('[Memory Analysis] Starting analysis...')
    this.seen.clear()
    this.components.clear()

    try {
      let rootVm = (document.querySelector('#app') as any)?.__vue__ // Vue 2

      if (!rootVm) {
        const app = (window as any).__VUE_APP__
        if (app) {
          rootVm = app._instance
        } else {
          rootVm = (window as any).__VUE_DEVTOOLS_GLOBAL_HOOK__?.Vue?._installedPlugins?.[0]?._vm
        }
      }

      if (!rootVm) {
        console.error('[Memory Analysis] No Vue instance found. Is this a Vue application?')
        return
      }

      this.analyzeComponent(rootVm)

      const tableData: MemoryStats[] = []

      Array.from(this.components.entries())
        .sort((a, b) => b[1].totalMemory - a[1].totalMemory)
        .forEach(([name, stats]) => {
          function formatBytes(bytes) {
            if (bytes === 0) return '0 Bytes'
            const k = 1024
            const sizes = ['Bytes', 'KB', 'MB', 'GB']
            const i = Math.floor(Math.log(bytes) / Math.log(k))
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
          }
          tableData.push({
            'Component Type': name,
            Instances: stats.count,
            'Total Memory': this.formatBytes(stats.totalMemory),
            'Avg Memory/Instance': this.formatBytes(stats.totalMemory / stats.count),
            'Total Watchers': stats.watchers,
            'Avg Watchers/Instance': (stats.watchers / stats.count).toFixed(1),
            'Data Keys': stats.dataKeys,
            'Visible/Hidden': `${stats.visible}/${stats.hidden}`,
            Type: 'Vue Component',
            Children: stats.children.map(c => ({ ...c, memory: formatBytes(c.memory) })),
            KeySizes: stats.keySizes.map(k => ({ ...k, memory: formatBytes(k.memory) })),
          })
        })

      if ((window.performance as any)?.memory) {
        tableData.push({
          'Component Type': 'Total JS Heap',
          Instances: 'N/A',
          'Total Memory': this.formatBytes((window.performance as any).memory.usedJSHeapSize),
          'Avg Memory/Instance': 'N/A',
          'Total Watchers': 'N/A',
          'Avg Watchers/Instance': 'N/A',
          'Data Keys': 'N/A',
          'Visible/Hidden': 'N/A',
          Type: 'System',
          Children: [],
          KeySizes: [],
        })
      }

      console.log('\n[Memory Analysis] Component Analysis:')
      console.table(tableData)
    } catch (error) {
      console.error('[Memory Analysis] Error:', error)
      console.error('Stack:', error.stack)
    }
  }
}
